// @flow

export const ERROR_PAGE_NAME = 'error page';
export const ADL_DEFAULT_VALUE = '-1';
export const ADL_DEFAULT_PAGE_NAME_NO_RESULTS = 'no results found';
export const ADL_DEFAULT_PAGE_NAME_WORLD_SEARCH = 'search_world';

export type AnalyticsDataLayerFieldType = string | '-1';

export const GTS_SORT_TO_ADL = {
    DEFAULT_SORT: 'listing_score',
    RATING_DESC: 'average_rating',
    BEDROOMS_DESC: 'bedrooms_high_to_low',
    BEDROOMS_ASC: 'bedrooms_low_to_high',
    PRICE_DESC: 'prices_high_to_low',
    PRICE_ASC: 'prices_low_to_high',
    NUM_REVIEWS_DESC: 'traveler_review_count'
};

export const BASE_ADL_FIELDS = {
    APP_ENV: 'appenvironment',
    APP_NAME: 'appname',
    APP_VERSION: 'appversion',
    DISPLAY_CURRENCY: 'displaycurrency',
    DISPLAY_LOCALE: 'displaylocale',
    MONIKER_BRAND: 'monikerbrand',
    MPAAS_REGION: 'mpaasregion',
    REQUEST_MARKER: 'requestmarker',
    SESSION_ID: 'sessionid',
    VISITOR_ID: 'visitorid'
};

export const LOCATION_ADL_FIELDS = {
    ADMIN_1: 'admin1',
    LATITUDE: 'latitude',
    LONGITUDE: 'longitude',
    SEARCHTERM: 'searchterm',
    GEO_TYPE: 'geotype',
    LBS_UUID: 'lbsuuid',
    POSTALCODE: 'postalcode',
    COUNTRY: 'country',
    LOCALITY: 'locality',
    ADMINISTRATIVE_DIVISION_LEVEL_1: 'administrative_division_level_1',
    POINT_OF_INTEREST: 'point_of_interest'
};

export const FILTERS_ADL_FIELDS = {
    REFINE_VALUE: 'refinevalue',
    REFINE_BUCKET: 'refinebucket',
    BOOKING: 'booking',
    PROPERTY_TYPE: 'propertytype',
    LOCATION_TYPE: 'locationtype',
    MISC: 'misc',
    FEATURES: 'features'
};

export const TRIPS_ADL_FIELDS = {
    KEYWORDS: 'keyword',
    NUM_ADULTS: 'numadults',
    NUM_CHILDREN: 'numchildren',
    NUM_INFANTS: 'numinfants',
    START_DATE: 'startdate',
    END_DATE: 'enddate',
    MIN_BATHS: 'minbaths',
    MAX_BATHS: 'maxbaths',
    MIN_BEDS: 'minbeds',
    MAX_BEDS: 'maxbeds',
    MIN_PRICE: 'minprice',
    MAX_PRICE: 'maxprice',
    MIN_SLEEPS: 'minsleeps',
    WITH_PETS: 'withpets',
    SORT: 'sort'
};

export const MISC_ADL_FIELDS = {
    ANALYTICS_BRAND: 'analyticsbrand',
    CURRENCY: 'currency',
    ITEM_5: 'item5',
    PAGE_FLOW: 'pageflow',
    PAGE_TYPE: 'pagetype',
    PUBLIC_UUID: 'publicuuid',
    SENSITIVE: 'sensitive',
    VISITOR_TYPE: 'visitortype',
    LISTING_IDS: 'listingids',
    NUM_RESULTS: 'numresults',
    NUM_RESULTS_DISPLAYED: 'numresultsdisplayed',
    PAGE_NAME: 'pagename',
    PAGINATION: 'pagination',
    PINNED_LISTINGS_IDS: 'pinnedlistingids',
    SEARCH_TERM: 'searchterm'
};

export const FILTER_GROUP_ID_TO_ADL = {
    '1': 'features',
    '4': 'misc',
    '3': 'locationtype',
    '6': 'propertytype',
    '9': 'booking',
    booking_title: 'booking'
};

export const FILTER_GROUP_KEY_TO_ID = {
    booking_title: '9',
    amenities_title: '2'
};

export type SortADLField = $Values<typeof GTS_SORT_TO_ADL> | '-1';

export type LocationADLShape = {|
    +admin1: AnalyticsDataLayerFieldType,
    +latitude: AnalyticsDataLayerFieldType,
    +longitude: AnalyticsDataLayerFieldType,
    +searchterm: AnalyticsDataLayerFieldType,
    +geotype: AnalyticsDataLayerFieldType,
    +lbsuuid: AnalyticsDataLayerFieldType,
    +postalcode: AnalyticsDataLayerFieldType,
    +country: AnalyticsDataLayerFieldType,
    +locality: AnalyticsDataLayerFieldType
|};

export type FiltersADLShape = {|
    +refinevalue: AnalyticsDataLayerFieldType,
    +refinebucket: AnalyticsDataLayerFieldType,
    +booking: AnalyticsDataLayerFieldType,
    +propertytype: AnalyticsDataLayerFieldType,
    +locationtype: AnalyticsDataLayerFieldType,
    +misc: AnalyticsDataLayerFieldType,
    +features: AnalyticsDataLayerFieldType
|};

export type TripADLShape = {|
    +keyword: AnalyticsDataLayerFieldType,
    +numadults: AnalyticsDataLayerFieldType,
    +numchildren: AnalyticsDataLayerFieldType,
    +numinfants: AnalyticsDataLayerFieldType,
    +startdate: AnalyticsDataLayerFieldType,
    +enddate: AnalyticsDataLayerFieldType,
    +minbaths: AnalyticsDataLayerFieldType,
    +maxbaths: AnalyticsDataLayerFieldType,
    +minbeds: AnalyticsDataLayerFieldType,
    +maxbeds: AnalyticsDataLayerFieldType,
    +minprice: AnalyticsDataLayerFieldType,
    +maxprice: AnalyticsDataLayerFieldType,
    +minsleeps: AnalyticsDataLayerFieldType,
    +withpets: AnalyticsDataLayerFieldType,
    +sort: SortADLField
|};

export type MiscADLShape = {|
    +listingids: AnalyticsDataLayerFieldType,
    +numresults: AnalyticsDataLayerFieldType,
    +numresultsdisplayed: AnalyticsDataLayerFieldType,
    +pagename: AnalyticsDataLayerFieldType,
    +pagination: AnalyticsDataLayerFieldType,
    +pinnedlistingids: AnalyticsDataLayerFieldType,
    +analyticsbrand: AnalyticsDataLayerFieldType,
    +currency: AnalyticsDataLayerFieldType,
    +item5: AnalyticsDataLayerFieldType,
    +pageflow: AnalyticsDataLayerFieldType,
    +pagetype: AnalyticsDataLayerFieldType,
    +publicuuid: AnalyticsDataLayerFieldType,
    +sensitive: AnalyticsDataLayerFieldType,
    +visitortype: AnalyticsDataLayerFieldType
|};

export type BaseADLShape = {|
    +appenvironment: AnalyticsDataLayerFieldType,
    +appname: AnalyticsDataLayerFieldType,
    +appversion: AnalyticsDataLayerFieldType,
    +mpaasregion: AnalyticsDataLayerFieldType,
    +monikerbrand: AnalyticsDataLayerFieldType,
    +displaycurrency: AnalyticsDataLayerFieldType,
    +displaylocale: AnalyticsDataLayerFieldType,
    +visitorid: AnalyticsDataLayerFieldType,
    +sessionid: AnalyticsDataLayerFieldType,
    +requestmarker: AnalyticsDataLayerFieldType
|};

export const ADL_DEFAULT_LOCATION_DATA: LocationADLShape = {
    [LOCATION_ADL_FIELDS.ADMIN_1]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.LATITUDE]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.LONGITUDE]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.SEARCHTERM]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.GEO_TYPE]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.LBS_UUID]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.POSTALCODE]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.COUNTRY]: ADL_DEFAULT_VALUE,
    [LOCATION_ADL_FIELDS.LOCALITY]: ADL_DEFAULT_VALUE
};

export const ADL_DEFAULT_FILTERS_DATA: FiltersADLShape = {
    [FILTERS_ADL_FIELDS.REFINE_VALUE]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.REFINE_BUCKET]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.BOOKING]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.PROPERTY_TYPE]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.LOCATION_TYPE]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.MISC]: ADL_DEFAULT_VALUE,
    [FILTERS_ADL_FIELDS.FEATURES]: ADL_DEFAULT_VALUE
};

export const ADL_DEFAULT_TRIP_DATA: TripADLShape = {
    [TRIPS_ADL_FIELDS.KEYWORDS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.NUM_ADULTS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.NUM_CHILDREN]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.NUM_INFANTS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.START_DATE]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.END_DATE]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MIN_BATHS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MAX_BATHS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MIN_BEDS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MAX_BEDS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MIN_PRICE]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MAX_PRICE]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.MIN_SLEEPS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.WITH_PETS]: ADL_DEFAULT_VALUE,
    [TRIPS_ADL_FIELDS.SORT]: ADL_DEFAULT_VALUE
};

export const ADL_DEFAULT_MISC_DATA: MiscADLShape = {
    [MISC_ADL_FIELDS.LISTING_IDS]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.NUM_RESULTS]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.NUM_RESULTS_DISPLAYED]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.PAGE_NAME]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.PAGINATION]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.PINNED_LISTINGS_IDS]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.ANALYTICS_BRAND]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.CURRENCY]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.ITEM_5]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.PAGE_FLOW]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.PAGE_TYPE]: 'search',
    [MISC_ADL_FIELDS.PUBLIC_UUID]: ADL_DEFAULT_VALUE,
    [MISC_ADL_FIELDS.SENSITIVE]: 'false',
    [MISC_ADL_FIELDS.VISITOR_TYPE]: 'traveler'
};

export const ADL_DEFAULT_BASE_DATA: BaseADLShape = {
    [BASE_ADL_FIELDS.APP_ENV]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.APP_NAME]: 'node-traveler-search-ui',
    [BASE_ADL_FIELDS.APP_VERSION]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.DISPLAY_CURRENCY]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.DISPLAY_LOCALE]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.MONIKER_BRAND]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.MPAAS_REGION]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.REQUEST_MARKER]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.SESSION_ID]: ADL_DEFAULT_VALUE,
    [BASE_ADL_FIELDS.VISITOR_ID]: ADL_DEFAULT_VALUE
};

export type AnalyticsDataLayerType = {|
    ...BaseADLShape,
    ...LocationADLShape,
    ...FiltersADLShape,
    ...TripADLShape,
    ...MiscADLShape
|};

export const INITIAL_ANALYTICS_DATA_LAYER: AnalyticsDataLayerType = {
    ...ADL_DEFAULT_BASE_DATA,
    ...ADL_DEFAULT_LOCATION_DATA,
    ...ADL_DEFAULT_FILTERS_DATA,
    ...ADL_DEFAULT_TRIP_DATA,
    ...ADL_DEFAULT_MISC_DATA
};
